import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/sheet';
import { createFileRoute } from '@tanstack/react-router';
import { Heart, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LimitReachedDialog,
  LoadingState,
  PageContainer,
  PageHeader,
} from '@/components/shared';
import { useCreateWishlistItem } from '@/hooks/mutations/use-create-wishlist-item';
import { useDeleteWishlistItem } from '@/hooks/mutations/use-delete-wishlist-item';
import { useUpdateWishlistItem } from '@/hooks/mutations/use-update-wishlist-item';
import {
  useRelationship,
  type WishlistItem,
} from '@/hooks/queries/use-relationship';
import { useUserMe } from '@/hooks/queries/use-user-me';

import {
  WishlistItemCard,
  WishlistItemForm,
  type WishlistItemFormValues,
} from './-components';

export const Route = createFileRoute('/(app)/wishlist/me')({
  component: MyWishlistPage,
});

const FREE_PLAN_LIMIT = 3;

function MyWishlistPage() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WishlistItem | undefined>();
  const [deletingItem, setDeletingItem] = useState<WishlistItem | undefined>();
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
  } = useUserMe();
  const relationshipId = userData?.relationships?.[0]?.id;
  const plan = userData?.plan ?? 'free';

  const {
    data: relationshipData,
    isLoading: isLoadingRelationship,
    error: relationshipError,
    refetch,
  } = useRelationship(relationshipId);

  const createMutation = useCreateWishlistItem(relationshipId ?? '');
  const updateMutation = useUpdateWishlistItem(relationshipId ?? '');
  const deleteMutation = useDeleteWishlistItem(relationshipId ?? '');

  const isLoading = isLoadingUser || isLoadingRelationship;
  const error = userError || relationshipError;

  const wishlist = relationshipData?.user_wishlist ?? [];
  const isFreePlan = plan === 'free';
  const canAddMore = !isFreePlan || wishlist.length < FREE_PLAN_LIMIT;

  const filteredItems = wishlist.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleAddClick = () => {
    if (!canAddMore) {
      setShowLimitDialog(true);
      return;
    }
    setEditingItem(undefined);
    setIsSheetOpen(true);
  };

  const handleEdit = (item: WishlistItem) => {
    setEditingItem(item);
    setIsSheetOpen(true);
  };

  const handleDelete = (item: WishlistItem) => {
    setDeletingItem(item);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setEditingItem(undefined);
  };

  const handleSubmit = async (data: WishlistItemFormValues) => {
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({
          itemId: editingItem.id,
          data,
        });
        toast.success('Item atualizado com sucesso!');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Item adicionado com sucesso!');
      }
      handleCloseSheet();
    } catch {
      toast.error('Ocorreu um erro. Tente novamente.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(deletingItem.id);
      toast.success('Item excluido com sucesso!');
      setDeletingItem(undefined);
    } catch {
      toast.error('Erro ao excluir item. Tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState message="Carregando sua lista..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState
          title="Erro ao carregar lista"
          message="Não foi possível carregar sua wishlist. Tente novamente."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Minha Lista"
          subtitle={
            isFreePlan
              ? `${wishlist.length} de ${FREE_PLAN_LIMIT} itens`
              : `${wishlist.length} itens`
          }
        />

        {wishlist.length > 0 && (
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Buscar presentes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {wishlist.length === 0 ? (
          <EmptyState
            icon={<Heart className="h-6 w-6" />}
            title="Sua lista está vazia"
            description="Adicione itens que você gostaria de ganhar para que seu parceiro possa ver."
            action={
              <Button onClick={handleAddClick}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar primeiro item
              </Button>
            }
          />
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={<Search className="h-6 w-6" />}
            title="Nenhum resultado"
            description="Não encontramos itens com esse termo."
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {filteredItems.map((item) => (
              <WishlistItemCard
                key={item.id}
                item={item}
                onClick={handleEdit}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {wishlist.length > 0 && (
        <div className="fixed bottom-24 left-1/2 z-40 -translate-x-1/2 md:bottom-8">
          <Button
            onClick={handleAddClick}
            className="rounded-full px-6 shadow-lg"
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar presente
          </Button>
        </div>
      )}

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent
          side="bottom"
          className="h-auto max-h-[90vh] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>
              {editingItem ? 'Editar item' : 'Adicionar item'}
            </SheetTitle>
            <SheetDescription>
              {editingItem
                ? 'Atualize as informações do item desejado.'
                : 'Adicione algo que você gostaria de ganhar.'}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <WishlistItemForm
              item={editingItem}
              onSubmit={handleSubmit}
              onCancel={handleCloseSheet}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!deletingItem}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingItem(undefined);
          }
        }}
        title="Excluir item"
        description={`Tem certeza que deseja excluir "${deletingItem?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />

      <LimitReachedDialog
        open={showLimitDialog}
        onOpenChange={setShowLimitDialog}
        feature="wishlist"
        limit={FREE_PLAN_LIMIT}
      />
    </PageContainer>
  );
}
